import React, { useMemo, useState } from 'react';
import { FieldMapping, DataType, SectionConfig } from '../../types/metadata';
import { DataTypeBadge } from '../shared/Badge';
import { FieldMappingModal } from './FieldMappingModal';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import { EmptyState } from '../shared/EmptyState';
import { Edit, Trash2, ChevronUp, ChevronDown, Search, Plus, Eye, Star, FileText } from 'lucide-react';
interface FieldMappingListProps {
  fields: FieldMapping[];
  sections: SectionConfig[];
  selectedSectionKey: string | null;
  getOptionCount: (key: string) => number;
  onAdd: (field: Omit<FieldMapping, 'id'>) => void;
  onUpdate: (id: string, updates: Partial<FieldMapping>) => void;
  onDelete: (id: string) => void;
  onReorder: (id: string, direction: 'up' | 'down') => void;
}
export function FieldMappingList({
  fields,
  sections,
  selectedSectionKey,
  getOptionCount,
  onAdd,
  onUpdate,
  onDelete,
  onReorder
}: FieldMappingListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<DataType | 'All'>('All');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingField, setEditingField] = useState<FieldMapping | undefined>();
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const filteredFields = useMemo(() => {
    let result = fields;
    if (selectedSectionKey) {
      result = result.filter(f => f.section === selectedSectionKey);
    }
    if (filterType !== 'All') {
      result = result.filter(f => f.dataType === filterType);
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(f => f.promptText.toLowerCase().includes(term) || f.questionKey.toLowerCase().includes(term) || f.targetFieldApiName?.toLowerCase().includes(term));
    }
    return result.sort((a, b) => a.positionInSection - b.positionInSection);
  }, [fields, selectedSectionKey, filterType, searchTerm]);
  const handleEdit = (field: FieldMapping) => {
    setEditingField(field);
    setIsModalOpen(true);
  };
  const handleSave = (data: any) => {
    if (editingField) {
      onUpdate(editingField.id, data);
    } else {
      onAdd(data);
    }
  };
  const selectedSection = sections.find(s => s.sectionKey === selectedSectionKey);
  return <div className="flex-1 flex flex-col" data-id="element-4">
      {/* Header / Toolbar */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 mb-4 flex-shrink-0" data-id="element-5">
        <div className="flex flex-col sm:flex-row justify-between gap-3" data-id="element-6">
          <div className="flex flex-1 gap-3" data-id="element-7">
            <div className="relative max-w-sm flex-1" data-id="element-8">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-9">
                <Search className="h-4 w-4 text-gray-400" data-id="element-10" />
              </div>
              <input type="text" placeholder="Search fields..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-gray-400" data-id="element-11" />
            </div>
            <select value={filterType} onChange={e => setFilterType(e.target.value as any)} className="block w-40 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-12">
              <option value="All" data-id="element-13">All Types</option>
              {Object.values(DataType).map(type => <option key={type} value={type} data-id="element-14">
                  {type}
                </option>)}
            </select>
          </div>
          <button onClick={() => {
          setEditingField(undefined);
          setIsModalOpen(true);
        }} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors" data-id="element-15">
            <Plus className="h-4 w-4 mr-2" data-id="element-16" />
            New Field
          </button>
        </div>

        {selectedSection && <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 text-sm" data-id="element-17">
            <span className="font-medium text-gray-500" data-id="element-18">Section:</span>
            <span className="font-medium text-gray-900" data-id="element-19">
              {selectedSection.sectionLabel}
            </span>
            <span className="font-mono text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded" data-id="element-20">
              {selectedSection.sectionKey}
            </span>
          </div>}
      </div>

      {/* Table Area */}
      <div className="bg-white shadow-sm rounded-xl border border-gray-200 flex-1 overflow-hidden flex flex-col" data-id="element-21">
        {filteredFields.length > 0 ? <div className="overflow-auto flex-1" data-id="element-22">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-23">
              <thead className="bg-gray-50 sticky top-0 z-10" data-id="element-24">
                <tr data-id="element-25">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-20" data-id="element-26">
                    Position
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-27">
                    Field Details
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-28">
                    Type & Target
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-29">
                    Flags
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-20" data-id="element-30">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 w-20" data-id="element-31">
                    <span className="sr-only" data-id="element-32">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-33">
                {filteredFields.map((field, index) => {
              const hasCondition = !!field.conditionKey;
              const isPicklist = field.dataType === DataType.Picklist;
              const optionCount = isPicklist ? getOptionCount(field.questionKey) : 0;
              return <tr key={field.id} className="hover:bg-blue-50/40 transition-colors group cursor-pointer" onClick={() => handleEdit(field)} data-id="element-34">
                      <td className="px-6 py-3.5 whitespace-nowrap" onClick={e => e.stopPropagation()} data-id="element-35">
                        <div className="flex items-center gap-1.5" data-id="element-36">
                          <span className="text-sm font-medium text-gray-900 w-5 tabular-nums" data-id="element-37">
                            {field.positionInSection}
                          </span>
                          {selectedSectionKey && <div className="flex flex-col" data-id="element-38">
                              <button onClick={() => onReorder(field.id, 'up')} disabled={index === 0} className="text-gray-300 hover:text-blue-600 disabled:opacity-20 transition-colors p-0.5" data-id="element-39">
                                <ChevronUp className="h-3.5 w-3.5" data-id="element-40" />
                              </button>
                              <button onClick={() => onReorder(field.id, 'down')} disabled={index === filteredFields.length - 1} className="text-gray-300 hover:text-blue-600 disabled:opacity-20 transition-colors p-0.5" data-id="element-41">
                                <ChevronDown className="h-3.5 w-3.5" data-id="element-42" />
                              </button>
                            </div>}
                        </div>
                      </td>
                      <td className="px-6 py-3.5" data-id="element-43">
                        <div className="flex flex-col" data-id="element-44">
                          <span className="text-sm font-medium text-gray-900" data-id="element-45">
                            {field.promptText}
                          </span>
                          <span className="text-xs text-gray-400 font-mono mt-0.5" data-id="element-46">
                            {field.questionKey}
                          </span>
                          {!selectedSectionKey && <span className="text-xs text-blue-600 mt-1 font-medium" data-id="element-47">
                              {field.section}
                            </span>}
                        </div>
                      </td>
                      <td className="px-6 py-3.5 whitespace-nowrap" data-id="element-48">
                        <div className="flex flex-col gap-1.5 items-start" data-id="element-49">
                          <DataTypeBadge type={field.dataType} data-id="element-50" />
                          {field.targetFieldApiName && <span className="text-xs text-gray-400 font-mono" data-id="element-51">
                              {field.targetFieldApiName}
                            </span>}
                        </div>
                      </td>
                      <td className="px-6 py-3.5 whitespace-nowrap" data-id="element-52">
                        <div className="flex items-center gap-1.5" data-id="element-53">
                          {field.required && <span title="Required" className="flex items-center justify-center w-6 h-6 rounded-full bg-red-50 text-red-500" data-id="element-54">
                              <Star className="w-3 h-3 fill-current" data-id="element-55" />
                            </span>}
                          {hasCondition && <span title={`Condition: ${field.conditionKey} = ${field.conditionValue}`} className="flex items-center justify-center w-6 h-6 rounded-full bg-amber-50 text-amber-500" data-id="element-56">
                              <Eye className="w-3 h-3" data-id="element-57" />
                            </span>}
                          {isPicklist && <span title={`${optionCount} options`} className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 tabular-nums" data-id="element-58">
                              {optionCount} opt
                            </span>}
                        </div>
                      </td>
                      <td className="px-6 py-3.5 whitespace-nowrap" onClick={e => e.stopPropagation()} data-id="element-59">
                        <button onClick={() => onUpdate(field.id, {
                    active: !field.active
                  })} className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${field.active ? 'bg-green-500' : 'bg-gray-300'}`} aria-label={field.active ? 'Deactivate' : 'Activate'} data-id="element-60">
                          <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${field.active ? 'translate-x-4' : 'translate-x-0'}`} data-id="element-61" />
                        </button>
                      </td>
                      <td className="px-6 py-3.5 whitespace-nowrap text-right" onClick={e => e.stopPropagation()} data-id="element-62">
                        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-63">
                          <button onClick={() => handleEdit(field)} className="text-gray-400 hover:text-blue-600 p-1.5 rounded-lg hover:bg-blue-50 transition-colors" title="Edit" data-id="element-64">
                            <Edit className="h-4 w-4" data-id="element-65" />
                          </button>
                          <button onClick={() => setDeleteId(field.id)} className="text-gray-400 hover:text-red-600 p-1.5 rounded-lg hover:bg-red-50 transition-colors" title="Delete" data-id="element-66">
                            <Trash2 className="h-4 w-4" data-id="element-67" />
                          </button>
                        </div>
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div> : <div className="flex-1 flex items-center justify-center p-8" data-id="element-68">
            <EmptyState icon={FileText} title="No field mappings found" description={searchTerm ? 'Try adjusting your search or filters.' : 'Create your first field mapping for this section.'} action={!searchTerm ? <button onClick={() => {
          setEditingField(undefined);
          setIsModalOpen(true);
        }} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 transition-colors" data-id="element-70">
                    <Plus className="h-4 w-4 mr-2" data-id="element-71" />
                    Create Field
                  </button> : undefined} data-id="element-69" />
          </div>}
      </div>

      <FieldMappingModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSave={handleSave} initialData={editingField} sections={sections} preselectedSection={selectedSectionKey} data-id="element-72" />

      <ConfirmDialog isOpen={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={() => deleteId && onDelete(deleteId)} title="Delete Field Mapping" message="Are you sure you want to delete this field mapping? This action cannot be undone." confirmLabel="Delete Field" data-id="element-73" />
    </div>;
}